import { useState, useEffect, useRef, type FC } from 'react';
import {
  Show,
  SimpleShowLayout,
  TextField,
  DateField,
  ReferenceField,
  TranslatableFields,
  TopToolbar,
  ListButton,
  Button,
  useRecordContext,
  useDataProvider,
  useNotify,
  useRefresh,
} from 'react-admin';
import ReplayIcon from '@mui/icons-material/Replay';
import { Link } from 'react-router-dom';
import { Chip, Box, Typography, LinearProgress, FormControlLabel, Switch } from '@mui/material';
import { TemplateVersionField } from './TemplateVersionField';
import type { Build, BuildStatus, BuildLog } from '../../types';
import type { LogtoDataProvider } from '../../dataProvider';
import { APP_LOCALES, DEFAULT_LOCALE } from '../../locales';

// `label` is read by SimpleShowLayout for the field caption.
const PublishModeField: FC<{ label?: string }> = () => {
  const record = useRecordContext<Build>();
  if (!record) return null;
  if (record.skipPublish) return <Chip size="small" label="Build only" />;
  const stores = [
    record.publishAndroid && (record.testingOnly ? 'Google Play internal testing' : 'Google Play'),
    record.publishIos && (record.testingOnly ? 'TestFlight' : 'App Store'),
  ].filter(Boolean);
  return (
    <Chip
      size="small"
      color={record.testingOnly ? 'info' : 'primary'}
      label={`${record.testingOnly ? 'Testing' : 'Release'}: ${stores.join(', ') || '—'}`}
    />
  );
};

const ArtifactLinks = () => {
  const record = useRecordContext<Build>();
  if (!record) return null;
  const { artifactAabUrl, artifactApkUrl, artifactIpaUrl } = record as any;
  if (!artifactAabUrl && !artifactApkUrl && !artifactIpaUrl) return null;

  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" color="text.secondary">Artifacts</Typography>
      <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
        {artifactAabUrl && (
          <Chip
            label="Download AAB"
            component="a"
            href={artifactAabUrl}
            clickable
            color="success"
            size="small"
          />
        )}
        {artifactApkUrl && (
          <Chip
            label="Download APK"
            component="a"
            href={artifactApkUrl}
            clickable
            color="success"
            size="small"
          />
        )}
        {artifactIpaUrl && (
          <Chip
            label="Download IPA"
            component="a"
            href={artifactIpaUrl}
            clickable
            color="info"
            size="small"
          />
        )}
      </Box>
    </Box>
  );
};

// assetlinks.json and apple-app-site-association the worker generated for
// this client: the store's website serves them from /.well-known/ so links to
// it open the app.
const SiteFileLinks = () => {
  const record = useRecordContext<Build>();
  if (!record) return null;
  const { artifactAssetlinksUrl, artifactAasaUrl } = record;
  if (!artifactAssetlinksUrl && !artifactAasaUrl) return null;

  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" color="text.secondary">Website files</Typography>
      <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
        {artifactAssetlinksUrl && (
          <Chip
            label="assetlinks.json"
            component="a"
            href={artifactAssetlinksUrl}
            clickable
            color="success"
            size="small"
          />
        )}
        {artifactAasaUrl && (
          <Chip
            label="apple-app-site-association"
            component="a"
            href={artifactAasaUrl}
            clickable
            color="info"
            size="small"
          />
        )}
      </Box>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
        Serve both from https://&lt;store domain&gt;/.well-known/ (status 200, no redirects,
        Content-Type: application/json) so links to the site open the app.
      </Typography>
    </Box>
  );
};

const StatusField = () => {
  const record = useRecordContext<Build>();
  if (!record) return null;

  const statusColors: Record<
    BuildStatus,
    'default' | 'info' | 'warning' | 'success' | 'error'
  > = {
    queued: 'default',
    preparing: 'info',
    in_progress: 'warning',
    building: 'warning',
    uploading: 'info',
    publishing: 'info',
    completed: 'success',
    failed: 'error',
    timeout: 'error',
  };

  return (
    <Chip
      label={record.status}
      color={statusColors[record.status]}
      size="small"
    />
  );
};

const BuildLogsSection = () => {
  const record = useRecordContext<Build>();
  const dataProvider = useDataProvider<LogtoDataProvider>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [logs, setLogs] = useState<BuildLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [currentStatus, setCurrentStatus] = useState<BuildStatus | null>(null);

  const isStatusActive = (status: BuildStatus | null | undefined) =>
    status ? ['queued', 'preparing', 'in_progress', 'building', 'uploading', 'publishing'].includes(status) : false;

  // Fetch logs and refresh record
  const fetchLogsAndRefresh = () => {
    if (!record?.id) return;

    // Fetch logs
    dataProvider
      .getBuildLogs(record.id as string, { limit: 500 })
      .then(({ data }) => {
        setLogs(data as BuildLog[]);
      })
      .catch(() => {
        if (logs.length === 0) {
          notify('Failed to load build logs', { type: 'error' });
        }
      })
      .finally(() => {
        setLoading(false);
      });

    // Fetch current build status and refresh if changed
    dataProvider
      .getOne<Build>('builds', { id: record.id })
      .then(({ data }) => {
        const newStatus = data.status;
        // If status changed, refresh the whole record to update all fields
        if (currentStatus !== null && currentStatus !== newStatus) {
          refresh();
        }
        setCurrentStatus(newStatus);
      })
      .catch(() => {
        // Ignore status fetch errors
      });
  };

  // Initial load
  useEffect(() => {
    setLoading(true);
    if (record?.status) {
      setCurrentStatus(record.status);
    }
    fetchLogsAndRefresh();
  }, [record?.id]);

  // Auto-refresh when build is in progress
  useEffect(() => {
    if (!autoRefresh || !record?.id) return;

    // Only poll when build is active
    if (!isStatusActive(currentStatus || record?.status)) return;

    const interval = setInterval(fetchLogsAndRefresh, 2000);
    return () => clearInterval(interval);
  }, [record?.id, currentStatus, autoRefresh]);

  const logsContainerRef = useRef<HTMLDivElement>(null);
  const status = currentStatus || record?.status;
  const isActive = isStatusActive(status);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (autoRefresh && logsContainerRef.current) {
      logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
    }
  }, [logs, autoRefresh]);

  if (loading && logs.length === 0) {
    return <LinearProgress />;
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6">
          Build Logs {isActive && <Chip label="Live" color="success" size="small" sx={{ ml: 1 }} />}
        </Typography>
        {isActive && (
          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                size="small"
              />
            }
            label="Auto-refresh"
          />
        )}
      </Box>
      <Box
        ref={logsContainerRef}
        sx={{
          backgroundColor: '#1e1e1e',
          color: '#d4d4d4',
          p: 2,
          borderRadius: 1,
          fontFamily: 'monospace',
          fontSize: '0.85rem',
          maxHeight: '500px',
          overflow: 'auto',
          lineHeight: 1.4,
        }}
      >
        {logs.length === 0 ? (
          <Typography color="inherit" sx={{ opacity: 0.7 }}>No logs available</Typography>
        ) : (
          logs.map((log) => (
            <Box key={log.id} sx={{ mb: 0.25 }}>
              <Typography
                component="span"
                color="inherit"
                sx={{ opacity: 0.5, mr: 1, fontSize: '0.75rem' }}
              >
                {new Date(log.timestamp).toLocaleTimeString()}
              </Typography>
              <Typography
                component="span"
                color="inherit"
                sx={{
                  color: log.level === 'error' ? '#f44336' :
                         log.level === 'warning' ? '#ff9800' :
                         log.level === 'output' ? '#90caf9' : '#d4d4d4'
                }}
              >
                {log.message}
              </Typography>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

// RetryBuildButton sends the user to /builds/create with the current build's
// client and release notes pre-filled via state (react-admin's standard clone
// pattern). The user reviews the form and submits to enqueue a fresh build.
const RetryBuildButton = () => {
  const record = useRecordContext<Build>();
  if (!record) return null;
  const cloneState = {
    record: {
      triggerType: 'manual',
      clientId: record.clientId,
      releaseNotes: record.releaseNotes,
      skipPublish: record.skipPublish ?? false,
      publishAndroid: record.publishAndroid ?? true,
      publishIos: record.publishIos ?? true,
      testingOnly: record.testingOnly ?? false,
      environment: record.environment || 'prod',
    },
  };
  return (
    <Button
      component={Link}
      to={{ pathname: '/builds/create' }}
      state={cloneState}
      label="Retry"
    >
      <ReplayIcon />
    </Button>
  );
};

const ShowActions = () => (
  <TopToolbar>
    <ListButton />
    <RetryBuildButton />
  </TopToolbar>
);

export const BuildShow = () => (
  <Show actions={<ShowActions />}>
    <SimpleShowLayout>
      <TextField source="id" />
      <ReferenceField source="clientId" reference="clients" label="Client" link="show">
        <TextField source="name" />
      </ReferenceField>
      <TemplateVersionField label="Template" />
      <TextField source="templateCommit" label="Template Commit" emptyText="—" />
      <TextField source="configVersion" label="Config Version" />
      <StatusField />
      <TextField source="triggerType" />
      <TextField source="environment" label="Environment" emptyText="prod" />
      <PublishModeField label="Publishing" />
      <TranslatableFields locales={APP_LOCALES} defaultLocale={DEFAULT_LOCALE}>
        <TextField source="releaseNotes" label="Release Notes" sx={{ whiteSpace: 'pre-line' }} />
      </TranslatableFields>
      <ArtifactLinks />
      <SiteFileLinks />
      <DateField source="createdAt" showTime />
      <DateField source="startedAt" showTime />
      <DateField source="completedAt" showTime />
      <BuildLogsSection />
    </SimpleShowLayout>
  </Show>
);
